import classnames from 'classnames'
import styles from './ProductGalleryListView.module.scss'
import SanityImage from '@/components/SanityImage/SanityImage'
import Link from '@/components/Link/Link'
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import useWindowResize from '@/hooks/use-window-resize'
import { ScrollTrigger } from 'gsap/dist/ScrollTrigger'
import gsap from 'gsap'
import { PRODUCT_GALLERY_BREAKPOINTS } from '@/sections/ProductGallery/ProductGallery'
import useStore from '@/store'
import { getProductLinkObject, lerp } from '@/utils'

ScrollTrigger.config({
  ignoreMobileResize: true,
})

const ProductGalleryListView = forwardRef(({ className, products, pageIsLeaving, allowListRender }, ref) => {
  const $container = useRef(null)
  const resizeKey = useWindowResize()
  const $itemImageContainers = useRef([])
  const $items = useRef([])
  const [topMargin, setTopMargin] = useState(0)
  const productGalleryType = useStore(state => state.productGalleryType)
  const productGalleryActiveFilter = useStore(state => state.productGalleryActiveFilter)
  const setProductGalleryActiveProductData = useStore(state => state.setProductGalleryActiveProductData)
  const itemHeightsLerped = useRef([])
  // const setDebugMobileText = useStore(state => state.setDebugMobileText)
  const options = useRef({
    maxHeight: 0,
    minHeight: 0,
    margin: 0,
    centerY: 0,
  })
  const lerpValue = useRef(1)

  const animateLerpValue = (zeroLerp = false) => {
    const animateTo = zeroLerp ? 1 : 0.25

    gsap.killTweensOf(lerpValue)
    gsap.to(lerpValue, {
      current: animateTo,
      duration: 0.25,
      ease: 'power3.inOut',
    })
  }

  const getBasicOptions = () => {
    const maxHeight = window.innerHeight * 0.55
    const minHeight = window.innerHeight * 0.2
    const margin = window.innerHeight * 0.55
    const centerY = window.innerHeight * 0.5

    const config = {
      maxHeight: maxHeight,
      minHeight: minHeight,
      margin,
      centerY,
    }

    return { config }
  }

  useEffect(() => {
    options.current = getBasicOptions()
  }, [resizeKey])

  const getActiveIndexFromPercentScrolled = percent => {
    let activeIndex = gsap.utils.mapRange(0, 1, 0, $items.current.length - 1, percent)
    activeIndex = gsap.utils.snap(1, activeIndex)
    return activeIndex
  }

  const getOffsetByPercentScrolled = percent => {
    const { config } = options.current
    const activeIndex = getActiveIndexFromPercentScrolled(percent)

    $items.current.forEach(($el, i) => {
      const $imageContainer = $itemImageContainers.current[i]
      if (!$imageContainer) return

      if (i === activeIndex && products[i]) {
        setProductGalleryActiveProductData(products[i])
      }
      // gsap.set($imageContainer, {
      //   outline: i === activeIndex ? '2px solid red' : 'none',
      // })
    })

    const itemOffsetsByIndex = {}
    $items.current.forEach((_, i) => {
      if (i === 0) {
        itemOffsetsByIndex[i] = 0
      } else {
        const offsetFromTop = i * window.innerHeight - config.margin * i
        itemOffsetsByIndex[i] = offsetFromTop
      }
    })

    const offset = itemOffsetsByIndex[activeIndex]

    return offset
  }

  const getActiveProductDataByPercentScrolled = percent => {
    const activeIndex = getActiveIndexFromPercentScrolled(percent)
    $items.current.forEach((_, i) => {
      if (i === activeIndex && products[i]) {
        setProductGalleryActiveProductData({
          ...products[i],
          index: i,
        })
      }
    })
  }

  const calculateOffsetOnScroll = () => {
    const offsets = []
    $itemImageContainers.current.forEach(($el, i) => {
      if (!$el) return

      const boundingRect = $el.getBoundingClientRect()

      offsets[i] = {
        top: boundingRect.top,
        left: boundingRect.left,
        width: $el.offsetWidth,
        height: $el.offsetHeight,
      }
    })

    return offsets
  }

  const raf = () => {
    const centerY = window.innerHeight * 0.5
    const { config } = options.current
    setTopMargin(config.margin * -1)

    $items.current.forEach(($el, i) => {
      const $imageContainer = $itemImageContainers.current[i]
      if (!$imageContainer) return

      const centerOffset = $el.getBoundingClientRect().top + $el.offsetHeight / 2
      let normalized = 1 - Math.abs((centerOffset - centerY) / window.innerHeight)
      normalized = gsap.utils.mapRange(0.55, 1, 0, 1, normalized)
      normalized = gsap.utils.clamp(0, 1, normalized)
      const height = gsap.utils.mapRange(0, 1, config.minHeight, config.maxHeight, normalized)

      // if (i === 2) {
      //   setDebugMobileText(`
      //     ${i} height: ${Math.round(height)}<br/>
      //     ${i} width: ${Math.round($el.scrollWidth)}<br/>
      //   `)
      // }

      if (!itemHeightsLerped.current[i]) {
        itemHeightsLerped.current[i] = {
          target: height,
          current: height,
        }
      }

      itemHeightsLerped.current[i].target = height
      itemHeightsLerped.current[i].current = lerp(
        itemHeightsLerped.current[i].current,
        itemHeightsLerped.current[i].target,
        lerpValue.current,
        // 0.25,
        // 1,
      )

      // on mobile the flip animation can be a lil jarrying because
      // of lerping. So when not on this view, just change the height
      // immediately

      gsap.set($imageContainer, {
        height: itemHeightsLerped.current[i].current,
        // height: productGalleryType === 'grid' ? height : itemHeightsLerped.current[i].current,
        // height,
      })
    })
  }

  useEffect(() => {
    gsap.ticker.remove(raf)

    if (pageIsLeaving) return

    raf()

    if (productGalleryType === 'list') {
      gsap.ticker.add(raf)
    }

    return () => {
      gsap.ticker.remove(raf)
    }
  }, [resizeKey, productGalleryType, pageIsLeaving])

  useImperativeHandle(ref, () => ({
    getElement: () => {
      return $container
    },
    getOffsetByPercentScrolled,
    calculateOffsetOnScroll,
    raf,
    getActiveProductDataByPercentScrolled,
    animateLerpValue,
  }))

  if (!allowListRender) return null

  return (
    <div
      className={classnames(styles.ProductGalleryListView, className)}
      ref={$container}
      style={{
        '--top-margin': `${topMargin}px`,
      }}
    >
      <ul className={styles.list}>
        {products.map((product, i) => (
          <li
            key={i}
            data-orientation={product.imageOrientation}
            className={classnames(styles.item, {
              [styles.notInFilter]:
                productGalleryActiveFilter && productGalleryActiveFilter !== product.productCategory.title,
            })}
            ref={ref => {
              $items.current[i] = ref
            }}
          >
            <Link
              link={getProductLinkObject(product.slug.current, product.title)}
              className={styles.itemLink}
            >
              <div
                className={styles.itemImageContainer}
                data-index={i}
                data-box-type="list"
                ref={ref => {
                  $itemImageContainers.current[i] = ref
                }}
              >
                <SanityImage
                  image={product.image}
                  className={styles.image}
                  breakpoints={PRODUCT_GALLERY_BREAKPOINTS}
                  disableLoadedOpacity
                  priority
                />
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
})

ProductGalleryListView.displayName = 'ProductGalleryListView'

export default ProductGalleryListView
